import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs';

interface CNavItem { label: string; path: string; match: string[]; }

/**
 * Customer app top nav bar (mockup `custNav`). Shown at the top of every customer
 * screen (Discover / My bookings / Majlis / Profile). On mobile the app-layout's
 * bottom-nav takes over; this bar is the desktop equivalent.
 */
@Component({
  selector: 'app-customer-nav',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './customer-nav.component.html',
  styleUrl: './customer-nav.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerNavComponent {
  private readonly router = inject(Router);

  readonly items: CNavItem[] = [
    { label: 'Discover',    path: '/customer/discover', match: ['/customer/discover', '/customer/vendor', '/customer/book'] },
    { label: 'My bookings', path: '/customer/bookings', match: ['/customer/bookings'] },
    { label: 'Majlis',      path: '/customer/invite',   match: ['/customer/invite', '/customer/card', '/customer/rsvp', '/customer/checklist'] },
    { label: 'Profile',     path: '/customer/profile',  match: ['/customer/profile'] },
  ];

  private readonly url = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(() => this.router.url.split('?')[0]),
      startWith(this.router.url.split('?')[0]),
    ),
    { initialValue: this.router.url.split('?')[0] },
  );

  isActive(item: CNavItem): boolean {
    const url = this.url();
    // Match on path-segment boundaries so `/customer/book` (Discover's booking
    // flow) does NOT also match `/customer/bookings` (My bookings).
    return item.match.some(m => url === m || url.startsWith(m + '/'));
  }

  navigate(path: string): void { void this.router.navigateByUrl(path); }
}
